"use client";
import { logger } from "@/utils/logger";

import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Play, ArrowRight, Activity, TrendingUp, Briefcase, ShieldCheck, HeartPulse, Stethoscope, Brain, HandHeart, Phone, Mail, ChevronRight } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useEffect, useState, useRef, Key, JSXElementConstructor, ReactElement, ReactNode, ReactPortal } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useSettings } from "@/context/SettingsContext";
import { useQuoteModal } from "@/context/QuoteModalContext";
import DOMPurify from 'isomorphic-dompurify';

let cachedPageContent: any = null;

export default function Home() {
  const router = useRouter();
  const settings = useSettings();
  const { openModal } = useQuoteModal();
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const [latestBlogs, setLatestBlogs] = useState<any[]>([]);
  const [pageContent, setPageContent] = useState<any>(cachedPageContent);
  const [services, setServices] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [approaches, setApproaches] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const approachesScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(true);
  const [expandedApproaches, setExpandedApproaches] = useState<Record<string, boolean>>({});

  const toggleApproach = (id: string | number) => {
    setExpandedApproaches(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleApproachesScroll = () => {
    if (approachesScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = approachesScrollRef.current;
      setShowLeftNav(scrollLeft > 10);
      setShowRightNav(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollApproaches = (direction: 'left' | 'right') => {
    if (approachesScrollRef.current) {
      const { clientWidth } = approachesScrollRef.current;
      // Scroll by 1 card width or clientWidth
      const scrollAmount = direction === 'left' ? -356 : 356;
      approachesScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const fetchData = async () => {
    try {
      const fetchJson = async (url: string) => {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) return { success: false, data: null };
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await res.json();
        }
        return { success: false, data: null };
      };

      const [blogsJson, contentJson, servicesJson, projectsJson, approachesJson] = await Promise.all([
        fetchJson('/api/public/blogs'),
        fetchJson('/api/admin/page-content?pageId=homepage'),
        fetchJson('/api/public/services'),
        fetchJson('/api/public/projects'),
        fetchJson('/api/public/approach')
      ]);

      if (blogsJson.success && blogsJson.data) {
        setLatestBlogs(blogsJson.data.slice(0, 3));
      }

      if (contentJson.success && contentJson.data && Object.keys(contentJson.data).length > 0) {
        if (JSON.stringify(cachedPageContent) !== JSON.stringify(contentJson.data)) {
          cachedPageContent = contentJson.data;
          setPageContent(contentJson.data);
        }
      }

      if (servicesJson.success && servicesJson.data) {
        setServices(servicesJson.data.slice(0, 4));
      }

      if (projectsJson.success && projectsJson.data) {
        setProjects(projectsJson.data.slice(0, 6));
      }

      if (approachesJson.success && approachesJson.data) {
        setApproaches(approachesJson.data);
      }
    } catch (err) {
      logger.error("Failed to fetch data", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh when tab becomes active
    const handleFocus = () => fetchData();
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // 1. GSAP: Slow, trustworthy floating animation for the background blobs
    if (blob1Ref.current && blob2Ref.current) {
      gsap.to(blob1Ref.current, {
        y: 40,
        x: 30,
        rotation: 10,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
      gsap.to(blob2Ref.current, {
        y: -50,
        x: -20,
        rotation: -5,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
    }

    const ctx = gsap.context(() => {
      // 2. SOLUTIONS / SERVICES SECTION
      gsap.fromTo(".fade-in-left",
        { opacity: 0, x: -50 },
        { opacity: 1, x: 0, duration: 0.8, scrollTrigger: { trigger: ".fade-in-left", once: true, start: "top 85%" } }
      );

      // 4. 3 STEPS / SERVICES SECTION
      // Step Title and Description text
      gsap.fromTo(".step-section-title",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", scrollTrigger: { trigger: ".step-section-title", once: true, start: "top 90%" } }
      );

      gsap.fromTo(".step-section-desc",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: "power2.out", scrollTrigger: { trigger: ".step-section-desc", once: true, start: "top 90%" } }
      );

      // Steps cards animation moved to dynamic useEffect

      // Rotating details animation moved to dynamic useEffect

      // Step char internal animation moved to a separate useEffect

      // 6. IDEA / INNOVATION BANNER
      gsap.to(".idea-bg", {
        scale: 1.05,
        duration: 20,
        yoyo: true,
        repeat: -1,
        ease: "linear"
      });

      gsap.fromTo(".idea-h2", { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: ".idea-h2", once: true, start: "top 90%" } });
      gsap.fromTo(".idea-p", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.2, scrollTrigger: { trigger: ".idea-h2", once: true, start: "top 90%" } });
      gsap.fromTo(".idea-btn", { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.5, delay: 0.4, scrollTrigger: { trigger: ".idea-h2", once: true, start: "top 90%" } });

    });

    return () => ctx.revert();
  }, []);

  // Dynamically trigger animation for approaches once data loads
  useEffect(() => {
    if (!isLoading || approaches.length > 0) {
      const timer = setTimeout(() => {
        // Step cards
        gsap.utils.toArray(".step-card").forEach((el: any, i) => {
          gsap.fromTo(el,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.6, delay: i * 0.2, scrollTrigger: { trigger: el, once: true, start: "top 85%" } }
          );
        });

        // Step card title animation removed (fades in entirely with the card)

        // Rotating details for new DOM nodes
        gsap.to(".rotating-details", {
          rotation: 360,
          duration: 12,
          repeat: -1,
          ease: "linear"
        });

        ScrollTrigger.refresh();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [approaches, isLoading]);

  // For Insights which render after fetch:
  useEffect(() => {
    if (latestBlogs.length > 0) {
      const ctx = gsap.context(() => {
        gsap.utils.toArray(".insight-card").forEach((el: any, i) => {
          gsap.fromTo(el,
            { opacity: 0, y: 40 },
            { opacity: 1, y: 0, duration: 0.6, delay: 0.1 * (i + 1), scrollTrigger: { trigger: el, once: true, start: "top 90%" } }
          );
        });
      });
      return () => ctx.revert();
    }
  }, [latestBlogs]);

  // For Services and Projects GSAP animations
  useEffect(() => {
    if (!isLoading) {
      const ctx = gsap.context(() => {
        gsap.utils.toArray(".stagger-card-up").forEach((el: any, i) => {
          gsap.fromTo(el,
            { opacity: 0, y: 50 },
            { opacity: 1, y: 0, duration: 0.6, delay: [0, 0.2, 0.1, 0.3][i] || 0, scrollTrigger: { trigger: el, once: true, start: "top 85%" } }
          );
        });

        gsap.utils.toArray(".team-card").forEach((el: any, i) => {
          gsap.fromTo(el,
            { opacity: 0, scale: 0.95 },
            { opacity: 1, scale: 1, duration: 0.6, delay: i * 0.1, scrollTrigger: { trigger: el, once: true, start: "top 85%" } }
          );
        });
      });
      return () => ctx.revert();
    }
  }, [isLoading]);

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden">

      {/* ══════════════════════════════════════════
          1. HERO SECTION
      ══════════════════════════════════════════ */}
      <section className="relative w-full h-screen min-h-[700px] lg:min-h-[850px] flex items-center justify-center overflow-hidden bg-[#111827]">
        {/* HD Background Image full cover */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/office_solutions_team.png"
            alt="Hero Background"
            fill
            sizes="100vw"
            className="object-cover opacity-80"
            priority
            quality={75}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* ════ DECORATIVE ORGANIC BLOBS (Matching Nifty Layout) ════ */}
        {/* Top Left Blob (Blue/Indigo) */}
        <div
          ref={blob1Ref}
          className="absolute -top-[10%] -left-[10%] w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#2a2a72]/80 mix-blend-multiply z-10"
          style={{ borderRadius: "50% 50% 50% 50% / 60% 40% 60% 40%" }}
        />

        {/* Right Edge Blob (Red Gradient) */}
        <div
          ref={blob2Ref}
          className="absolute top-[20%] -right-[15%] w-[350px] sm:w-[600px] h-[400px] sm:h-[700px] bg-gradient-to-br from-[#db0004]/90 to-[#6a0572]/90 mix-blend-multiply z-10"
          style={{ borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%" }}
        />

        {/* Bottom Left Blob (Orange Gradient) - Removed as requested */}

        {/* ════ HERO CONTENT (Centered) ════ */}
        <div className="max-w-[1000px] w-full mx-auto px-6 relative z-20 flex flex-col items-center justify-center text-center mt-12 pb-32">
          <h1 className="text-[40px] sm:text-[68px] lg:text-[84px] font-serif font-extrabold text-white leading-[1.05] tracking-tight mb-6 drop-shadow-md">
            {pageContent?.heroHeading || (
              <>
                Procurement, <br />
                Uncompromised.
              </>
            )}
          </h1>
          <p className="text-[16px] sm:text-[22px] lg:text-[24px] text-white/90 leading-relaxed font-medium drop-shadow-sm max-w-2xl mx-auto">
            {pageContent?.heroSubheading || "100% unbiased experts securing the exact services your business needs."}
          </p>
        </div>

        {/* ════ HERO SVG BOTTOM WAVE ════ */}
        <div className="absolute bottom-0 left-0 w-full overflow-visible leading-none z-20 pointer-events-none translate-y-[1px]">
          <svg className="relative block w-full h-[100px] md:h-[150px] lg:h-[200px] drop-shadow-[0_-8px_25px_rgba(255,255,255,0.15)]" viewBox="0 0 1200 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V120H0Z" fill="#f8f9fa"></path>
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          2. SOLUTIONS / SERVICES SECTION (Staggered Grid Layout)
      ══════════════════════════════════════════ */}
      <section className="relative w-full bg-[#f8f9fa] pb-[120px] pt-[2px] px-6 z-10 overflow-hidden">

        {/* Subtle decorative background dots */}
        <div className="absolute left-[40%] top-[40%] opacity-[0.03] pointer-events-none">
          <svg width="400" height="400" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#000" d="M42.7,-73.4C55.9,-65.7,67.7,-55.4,75.4,-42.6C83,-29.8,86.5,-14.9,85.2,-0.7C83.9,13.4,77.8,26.8,69.5,38.7C61.3,50.6,50.9,61,38.4,68.7C25.9,76.4,12.9,81.4,-0.6,82.4C-14.1,83.4,-28.3,80.4,-41.2,73C-54.2,65.6,-66,53.8,-73.6,40C-81.2,26.2,-84.6,10.4,-82.7,-4.4C-80.7,-19.2,-73.4,-33.1,-63.4,-44.2C-53.4,-55.2,-40.7,-63.5,-27.6,-70.7C-14.5,-77.9,-1.1,-84,13.2,-82.6C27.5,-81.1,41.7,-72,52.2,-61.1Z" transform="translate(100 100) scale(1.1)" />
          </svg>
        </div>

        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-start pt-0 relative z-10">

          {/* Left Column: Text & List */}
          <div
            className="fade-in-left max-w-[550px] opacity-0"
          >
            <h2 className="text-[40px] md:text-[56px] font-bold font-serif text-gray-900 leading-[1.1] mb-6 tracking-tight">
              {pageContent?.introTitle || "\"Don't just renew, have a review\""}
            </h2>
            <p className="text-[20px] md:text-[24px] text-gray-500 font-medium leading-[1.4] mb-8">
              Compare All can help. {settings.contactPhone?.trim() ? (
                <>Call us now on <a href={`tel:${settings.contactPhone}`} className="text-[#0f766e] hover:text-[#115e59] hover:underline transition-colors">{settings.contactPhone}</a></>
              ) : (
                <>Email us now at <a href={`mailto:${settings.contactEmail}`} className="text-[#0f766e] hover:text-[#115e59] hover:underline transition-colors">{settings.contactEmail}</a></>
              )} for a free initial consultation.
            </p>
            <p className="text-[15px] text-gray-500 leading-relaxed mb-10">
              {pageContent?.introContent || "Having researched your requirements we can assemble a package, which presents the best available mix of performance, price and service with recommendations for all the finance options that we can make available to you."}
            </p>

            {/* Custom Checkmark List */}
            <ul className="flex flex-col gap-4 mb-12">
              {(pageContent?.introFeatures || [
                "Performance Optimization",
                "Price Reduction",
                "Service Improvement",
                "Finance Options",
                "Unbiased Recommendations"
              ]).map((item: string, idx: number) => (
                <li key={idx} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-[1.5px] border-[#0d9488] flex items-center justify-center shrink-0 bg-transparent">
                    <svg className="w-3 h-3 text-[#0d9488]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[15px] text-gray-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>

            {(pageContent === null || (pageContent.ctaButtonText && pageContent.ctaButtonText.trim() !== '')) && (
              <Link
                href="/services"
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-full border border-gray-900 text-gray-900 font-semibold text-[15px] hover:bg-gray-900 hover:text-white transition-colors"
              >
                {pageContent?.ctaButtonText || "Explore Solutions"}
              </Link>
            )}
          </div>

          {/* Right Column: Staggered Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 relative">
            {/* Decorative dots behind cards */}
            <div className="absolute -left-16 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none">
              <svg width="150" height="150" viewBox="0 0 100 100" fill="currentColor" className="text-gray-400">
                <pattern id="dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                  <circle fill="currentColor" cx="2" cy="2" r="1.5"></circle>
                </pattern>
                <rect x="0" y="0" width="100" height="100" fill="url(#dots)"></rect>
              </svg>
            </div>

            {/* Left Column of Cards (Pushed down) */}
            <div className="flex flex-col gap-6 sm:mt-24">
              {isLoading ? (
                [1, 2].map((skeleton) => (
                  <div key={skeleton} className="bg-white rounded-[16px] overflow-hidden shadow-sm border border-gray-100">
                    <div className="relative h-[220px] w-full bg-gray-200 animate-pulse"></div>
                    <div className="p-8 flex flex-col gap-4">
                      <div className="h-6 bg-gray-300 animate-pulse rounded w-3/4"></div>
                      <div className="h-6 bg-gray-300 animate-pulse rounded w-1/2 mb-4"></div>
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-1/4"></div>
                    </div>
                  </div>
                ))
              ) : services.slice(0, 2).map((service, idx) => (
                <div key={service._id || idx} className="stagger-card-up bg-white rounded-[16px] overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-300 opacity-0">
                  <div className="relative h-[220px] w-full bg-slate-100">
                    {(() => {
                      const getValidImageUrl = (url: any) => (typeof url === 'string' && (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:image'))) ? url : null;
                      const imgSrc = getValidImageUrl(service.customImage) || getValidImageUrl(service.image) || getValidImageUrl(service.featuredImage);

                      return imgSrc ? (
                        <Image src={imgSrc} alt={service.title} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover" />
                      ) : (
                        <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${service.color || '#0d9488'}12` }}>
                          <div className="absolute inset-0 opacity-50" style={{ background: `radial-gradient(circle at top left, ${service.color || '#0d9488'}40, transparent 70%)` }}></div>
                          <div className="absolute inset-0 opacity-50" style={{ background: `radial-gradient(circle at bottom right, ${service.color || '#0d9488'}40, transparent 70%)` }}></div>
                          {(() => {
                            if (typeof service.icon === 'string' && service.icon.includes('<svg')) {
                              return <div className="w-16 h-16 opacity-90 relative z-10" style={{ color: service.color || '#0d9488' }} dangerouslySetInnerHTML={{ __html: service.icon }} />;
                            }
                            const IconComponent = typeof service.icon === 'string' ? (LucideIcons as any)[service.icon] : null;
                            if (IconComponent) {
                              return <IconComponent size={80} strokeWidth={1.5} style={{ color: service.color || '#0d9488' }} className="relative z-10 drop-shadow-sm transition-transform duration-500 hover:scale-110" />;
                            }
                            return (
                              <span className="text-[90px] font-serif font-black select-none relative z-10 leading-none" style={{ color: `${service.color || '#0d9488'}40` }}>
                                {service.title ? service.title.charAt(0).toUpperCase() : ''}
                              </span>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="p-8">
                    <h3 className="text-[20px] font-bold font-serif text-gray-900 leading-tight mb-8">
                      {service.title}
                    </h3>
                    <Link href={`/services/${service.slug}?from=home`} aria-label={`Read more about ${service.title}`} className="inline-flex items-center gap-2 text-[11px] font-bold text-gray-500 hover:text-[#0d9488] uppercase tracking-wider transition-colors">
                      Read More<span className="sr-only"> about {service.title}</span>
                      <div className="w-5 h-5 rounded-full border border-current flex items-center justify-center">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column of Cards (Top aligned) */}
            <div className="flex flex-col gap-6 sm:mt-0">
              {isLoading ? (
                [1, 2].map((skeleton) => (
                  <div key={skeleton} className="bg-white rounded-[16px] overflow-hidden shadow-sm border border-gray-100">
                    <div className="relative h-[220px] w-full bg-gray-200 animate-pulse"></div>
                    <div className="p-8 flex flex-col gap-4">
                      <div className="h-6 bg-gray-300 animate-pulse rounded w-3/4"></div>
                      <div className="h-6 bg-gray-300 animate-pulse rounded w-1/2 mb-4"></div>
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-1/4"></div>
                    </div>
                  </div>
                ))
              ) : services.slice(2, 4).map((service, idx) => (
                <div key={service._id || idx} className="stagger-card-up bg-white rounded-[16px] overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-300 opacity-0">
                  <div className="relative h-[220px] w-full bg-slate-100">
                    {(() => {
                      const getValidImageUrl = (url: any) => (typeof url === 'string' && (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:image'))) ? url : null;
                      const imgSrc = getValidImageUrl(service.customImage) || getValidImageUrl(service.image) || getValidImageUrl(service.featuredImage);

                      return imgSrc ? (
                        <Image src={imgSrc} alt={service.title} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover" />
                      ) : (
                        <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${service.color || '#0d9488'}12` }}>
                          <div className="absolute inset-0 opacity-50" style={{ background: `radial-gradient(circle at top left, ${service.color || '#0d9488'}40, transparent 70%)` }}></div>
                          <div className="absolute inset-0 opacity-50" style={{ background: `radial-gradient(circle at bottom right, ${service.color || '#0d9488'}40, transparent 70%)` }}></div>
                          {(() => {
                            if (typeof service.icon === 'string' && service.icon.includes('<svg')) {
                              return <div className="w-16 h-16 opacity-90 relative z-10" style={{ color: service.color || '#0d9488' }} dangerouslySetInnerHTML={{ __html: service.icon }} />;
                            }
                            const IconComponent = typeof service.icon === 'string' ? (LucideIcons as any)[service.icon] : null;
                            if (IconComponent) {
                              return <IconComponent size={80} strokeWidth={1.5} style={{ color: service.color || '#0d9488' }} className="relative z-10 drop-shadow-sm transition-transform duration-500 hover:scale-110" />;
                            }
                            return (
                              <span className="text-[90px] font-serif font-black select-none relative z-10 leading-none" style={{ color: `${service.color || '#0d9488'}40` }}>
                                {service.title ? service.title.charAt(0).toUpperCase() : ''}
                              </span>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="p-8">
                    <h3 className="text-[20px] font-bold font-serif text-gray-900 leading-tight mb-8">
                      {service.title}
                    </h3>
                    <Link href={`/services/${service.slug}?from=home`} aria-label={`Read more about ${service.title}`} className="inline-flex items-center gap-2 text-[11px] font-bold text-gray-500 hover:text-[#0d9488] uppercase tracking-wider transition-colors">
                      Read More<span className="sr-only"> about {service.title}</span>
                      <div className="w-5 h-5 rounded-full border border-current flex items-center justify-center">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════
          4. 3 STEPS / SERVICES SECTION (Matches Recruitment Screenshot Layout)
      ══════════════════════════════════════════ */}
      <section className="py-[120px] bg-white relative z-20 overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 text-center">

          <h2 className="step-section-title opacity-0 text-[36px] md:text-[44px] font-serif font-bold text-gray-900 leading-tight mb-4 flex justify-center flex-wrap">
            Steps of Our Process
          </h2>
          <p className="step-section-desc opacity-0 text-[17px] text-gray-500 font-medium mb-20 max-w-[700px] mx-auto flex flex-wrap justify-center gap-[0.25em]">
            To find out more or for a free, no obligation consultation please {settings.contactPhone?.trim() ? <>call <a href={`tel:${settings.contactPhone}`} className="text-[#0f766e] hover:text-[#115e59] hover:underline transition-colors">{settings.contactPhone}</a> or email us at</> : "email us at"}
            <a href={`mailto:${settings.contactEmail}`} className="text-[#0f766e] hover:text-[#115e59] hover:underline transition-colors">{settings.contactEmail}</a>
          </p>

          <div className="relative w-full md:px-16 xl:px-0">
            {approaches.length > 3 && (
              <>
                <button
                  onClick={() => scrollApproaches('left')}
                  aria-label="Scroll left"
                  className={`hidden md:flex absolute left-0 xl:-left-20 top-1/2 -translate-y-1/2 -mt-4 z-30 w-14 h-14 bg-white shadow-lg border border-gray-100 rounded-full items-center justify-center text-[#0d9488] hover:bg-[#0d9488] hover:text-white hover:scale-110 transition-all duration-300 ${!showLeftNav ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                >
                  <LucideIcons.ChevronLeft size={32} />
                </button>
                <button
                  onClick={() => scrollApproaches('right')}
                  aria-label="Scroll right"
                  className={`hidden md:flex absolute right-0 xl:-right-20 top-1/2 -translate-y-1/2 -mt-4 z-30 w-14 h-14 bg-white shadow-lg border border-gray-100 rounded-full items-center justify-center text-[#0d9488] hover:bg-[#0d9488] hover:text-white hover:scale-110 transition-all duration-300 ${!showRightNav ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                >
                  <LucideIcons.ChevronRight size={32} />
                </button>
              </>
            )}

            <div
              ref={approachesScrollRef}
              onScroll={handleApproachesScroll}
              className="w-full overflow-x-auto pt-6 pb-8 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <div className={`relative flex flex-row items-start gap-4 px-4 md:px-10 ${approaches.length <= 3 ? 'justify-center w-full' : 'justify-start w-max'}`}>

                {/* The horizontal connecting line (desktop only) - spans between center of first and last item */}
                <div className="hidden md:block absolute top-[35px] left-[200px] right-[200px] h-[2px] bg-gray-100 z-0"></div>

                {(approaches.length > 0 ? approaches : [
                  {
                    title: "Discover Your Vision",
                    description: "We begin by clarifying your personal and professional aspirations, so we can map out a clear path forward.",
                    icon: "Map"
                  },
                  {
                    title: "Set Meaningful Goals",
                    description: "Together, we'll establish achievable and impactful goals, aligned with your true desires and values.",
                    icon: "ClipboardCheck"
                  },
                  {
                    title: "Create a Plan",
                    description: "Based on your goals, we'll develop a tailored action plan that fits your unique journey and lifestyle.",
                    icon: "Layout"
                  }
                ]).map((step: any, idx: number) => {
                  const icons = [
                    <svg key={0} className="w-12 h-12 text-[#1c2c3c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>,
                    <svg key={1} className="w-12 h-12 text-[#1c2c3c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                    <svg key={2} className="w-12 h-12 text-[#1c2c3c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
                    <svg key={3} className="w-12 h-12 text-[#1c2c3c]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  ];
                  return (
                    <div key={step._id || idx} className="step-card opacity-0 relative z-10 flex flex-col items-center text-center w-[85vw] max-w-[320px] md:max-w-none md:w-[320px] shrink-0 snap-center mb-12 md:mb-0 group px-2">

                      {/* Icon Container with white background to break the line */}
                      <div className="w-[70px] h-[70px] bg-white flex items-center justify-center mb-6 relative">
                        {/* Rotating wrapper for decorative geometric details */}
                        <div className="rotating-details absolute inset-0 pointer-events-none rounded-full">
                          <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-[#0d9488]"></div>
                          <div className="absolute bottom-2 left-1 w-1 h-1 bg-[#db0004] rounded-full"></div>
                          <div className="absolute top-4 -left-2 text-[12px] text-gray-400 font-bold">+</div>
                          <div className="absolute bottom-4 -right-3 w-1.5 h-1.5 border border-gray-400 rounded-sm"></div>
                        </div>

                        {(() => {
                          const IconComponent = step.icon && (LucideIcons as any)[step.icon]
                            ? (LucideIcons as any)[step.icon]
                            : null;
                          if (IconComponent) return <IconComponent className="w-12 h-12 text-[#1c2c3c]" strokeWidth={1.5} />;
                          return icons[idx % icons.length];
                        })()}
                      </div>

                      <h3 className="step-card-title text-[16px] font-bold font-serif text-gray-900 mb-3 px-6 leading-snug text-center">
                        {step.title}
                      </h3>
                      <p className={`text-gray-500 text-[13px] leading-relaxed font-medium px-4 ${expandedApproaches[step._id || idx] ? '' : 'line-clamp-3 overflow-hidden text-ellipsis'}`}>
                        {step.description || step.desc}
                      </p>
                      {(step.description || step.desc) && (step.description || step.desc).length > 80 && (
                        <button
                          onClick={() => toggleApproach(step._id || idx)}
                          className="mt-3 text-[#0f766e] text-[12px] font-bold hover:underline transition-all flex items-center justify-center gap-1"
                        >
                          {expandedApproaches[step._id || idx] ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-aos="fade-up" data-aos-delay="200">
            <div
              onClick={openModal}
              className="inline-flex items-center justify-center px-8 py-3 rounded-full border-[1.5px] border-[#0f766e] text-[#0f766e] font-bold text-[13px] hover:bg-[#0f766e] hover:text-white transition-colors tracking-wide cursor-pointer"
            >
              Start Your Journey
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          5. OUR TEAM SECTION (Matches Screenshot 1)
      ══════════════════════════════════════════ */}
      {(isLoading || projects.length > 0) && (
        <section className="pt-[40px] pb-[100px] bg-white relative">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-[36px] md:text-[44px] font-serif font-bold text-gray-900 leading-tight mb-3">
                Our Projects
              </h2>
              <p className="text-[17px] text-gray-600 font-medium max-w-3xl mx-auto">
                Explore our recent projects and case studies showcasing how we deliver measurable results and value to our clients across various industries.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {isLoading ? (
                [1, 2, 3, 4, 5, 6].map((skeleton) => (
                  <div key={skeleton} className="relative rounded-2xl overflow-hidden aspect-[3/4] max-h-[450px] bg-gray-100 border border-gray-200">
                    <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col justify-end h-1/2 bg-gradient-to-t from-gray-300 to-transparent">
                      <div className="h-3 bg-gray-400/50 animate-pulse rounded w-1/3 mb-2"></div>
                      <div className="h-6 bg-gray-400/50 animate-pulse rounded w-3/4 mb-4"></div>
                      <div className="h-[1px] w-full bg-gray-400/30 mb-4"></div>
                      <div className="h-[1px] w-full bg-gray-400/30 mb-4"></div>
                      <div className="h-3 bg-gray-400/50 animate-pulse rounded w-2/3 mb-6"></div>
                      <div className="h-8 bg-gray-400/50 animate-pulse rounded-full w-full"></div>
                    </div>
                  </div>
                ))
              ) : projects.map((project: any, idx: number) => (
                <div
                  onClick={() => router.push(`/our-projects#${project.slug || project._id}`)}
                  key={project._id || idx}
                  className="team-card opacity-0 relative rounded-2xl overflow-hidden group aspect-[3/4] max-h-[450px] block cursor-pointer"
                >
                  {/* Render dynamic image only if it exists, otherwise solid background to avoid broken image icons */}
                  {(() => {
                    const imageSrc = (project.images && project.images[0]) || project.featuredImage || project.imageUrl || project.img;
                    return imageSrc ? (
                      <Image src={imageSrc} alt={project.title} fill sizes="(max-width: 768px) 100vw, 50vw" className="object-cover" />
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-[#1c2c3c]"></div>
                    );
                  })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                  <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col justify-end h-full">
                    <span className="text-[12px] font-bold text-white/80 mb-1">{project.category?.name || "Project"}</span>
                    <h3 className="text-[26px] font-bold text-white mb-4">{project.title}</h3>

                    {/* Horizontal white divider */}
                    <div className="h-[1px] w-full bg-white/30 mb-4"></div>

                    <div className="flex items-center gap-4 text-white/80 text-[12px] mb-5 flex-wrap relative z-20">
                      {settings.contactPhone?.trim() && (
                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${settings.contactPhone}`; }} className="flex items-center gap-1 hover:text-[#0d9488] transition-colors cursor-pointer"><Phone className="w-3.5 h-3.5" /> {settings.contactPhone}</div>
                      )}
                      <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `mailto:${settings.contactEmail}`; }} className="flex items-center gap-1 hover:text-[#0d9488] transition-colors cursor-pointer"><Mail className="w-3.5 h-3.5" /> {settings.contactEmail}</div>
                    </div>
                    <div className="flex items-center justify-between mt-auto relative z-20">
                      <div className="flex gap-2">
                        {(() => {
                          const hasSocial = (settings.socialLinks?.twitter && settings.socialLinks.twitter.length > 5) ||
                            (settings.socialLinks?.facebook && settings.socialLinks.facebook.length > 5) ||
                            (settings.socialLinks?.instagram && settings.socialLinks.instagram.length > 5) ||
                            (settings.socialLinks?.linkedin && settings.socialLinks.linkedin.length > 5) ||
                            (settings.socialLinks?.youtube && settings.socialLinks.youtube.length > 5) ||
                            (settings.socialLinks?.whatsapp && settings.socialLinks.whatsapp.length > 5);

                          return !hasSocial ? (
                            <>
                              {settings.contactPhone?.trim() && (
                                <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${settings.contactPhone}`; }} className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer" title="Call Us"><Phone className="w-3.5 h-3.5" /></div>
                              )}
                              <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `mailto:${settings.contactEmail}`; }} className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer" title="Email Us"><Mail className="w-3.5 h-3.5" /></div>
                            </>
                          ) : (
                            <>
                              {settings.socialLinks?.twitter && settings.socialLinks.twitter.length > 5 && (
                                <a onClick={(e) => e.stopPropagation()} href={settings.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer">
                                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" /></svg>
                                </a>
                              )}
                              {settings.socialLinks?.linkedin && settings.socialLinks.linkedin.length > 5 && (
                                <a onClick={(e) => e.stopPropagation()} href={settings.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer">
                                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                                </a>
                              )}
                              {settings.socialLinks?.facebook && settings.socialLinks.facebook.length > 5 && (
                                <a onClick={(e) => e.stopPropagation()} href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer">
                                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>
                                </a>
                              )}
                              {settings.socialLinks?.instagram && settings.socialLinks.instagram.length > 5 && (
                                <a onClick={(e) => e.stopPropagation()} href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white hover:text-gray-900 text-white transition-colors cursor-pointer">
                                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                                </a>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="text-[11px] font-bold text-white uppercase tracking-wider flex items-center gap-1 group-hover:text-[#0d9488] transition-colors">
                        READ MORE <ChevronRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════
          6. IDEA / INNOVATION BANNER
      ══════════════════════════════════════════ */}
      <section className="relative w-full min-h-[600px] flex items-center py-[100px] overflow-hidden">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 z-0">
          <div
            className="idea-bg w-full h-full absolute inset-0 origin-center"
          >
            <Image
              src="/hero1.jpg"
              alt="Innovation Background"
              fill
              sizes="100vw"
              className="object-cover object-center"
            />
          </div>
          {/* Clean, professional, trust-building overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#111827] via-[#111827]/70 to-transparent opacity-90 pointer-events-none" />
        </div>

        {/* Content */}
        <div className="max-w-[1200px] w-full mx-auto px-6 relative z-10">
          <div className="max-w-[650px]">
            <h2
              className="idea-h2 opacity-0 text-[40px] md:text-[50px] lg:text-[56px] font-serif text-white leading-[1.1] mb-6 tracking-tight drop-shadow-md"
            >
              If you have the idea we will find the right way
            </h2>

            <p
              className="idea-p opacity-0 text-[16px] md:text-[18px] text-gray-200 leading-relaxed mb-10 font-medium max-w-[500px] drop-shadow-sm"
            >
              "Don't just renew, have a review" - Compare All can help.
            </p>

            <div
              className="idea-btn opacity-0"
            >
              <Link
                href="/services"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-[#0d9488] text-white rounded-full font-semibold text-[15px] hover:bg-[#0f766e] transition-colors shadow-lg shadow-[#0d9488]/20"
              >
                View all available services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          7. INSIGHTS / SERVICES CARDS (Matches Screenshot 4)
      ══════════════════════════════════════════ */}
      <section className="py-[120px] bg-white relative">
        <div className="max-w-[1200px] mx-auto px-6">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">

            {isLoading ? (
              [1, 2, 3].map((skeleton) => (
                <div key={skeleton} className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100">
                  <div className="relative h-[240px] w-full overflow-hidden bg-gray-300 animate-pulse"></div>
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="h-3 bg-gray-300 animate-pulse rounded w-1/4 mb-4"></div>
                    <div className="h-6 bg-gray-300 animate-pulse rounded w-full mb-2"></div>
                    <div className="h-6 bg-gray-300 animate-pulse rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-full mb-2 flex-grow mt-2"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-5/6 mb-8"></div>
                    <div className="h-4 bg-gray-300 animate-pulse rounded w-1/3 mt-auto"></div>
                  </div>
                </div>
              ))
            ) : latestBlogs.length > 0 ? (
              latestBlogs.map((blog, idx) => (
                <div key={blog._id} className="insight-card opacity-0 group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-300 border border-gray-100">
                  <div className="relative h-[240px] w-full overflow-hidden">
                    <Image src={blog.featuredImage || blog.imageUrl || "/office_solutions_team.png"} alt={blog.title} fill sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" className="object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-8 flex flex-col flex-grow">
                    <span className="text-[12px] font-bold text-gray-500 mb-2">
                      {new Date(blog.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                    <h3 className="text-[24px] font-bold text-gray-900 leading-tight mb-4 group-hover:text-[#0d9488] transition-colors line-clamp-2" title={blog.title}>
                      {blog.title}
                    </h3>
                    <div className="text-[14px] text-gray-500 mb-8 flex-grow leading-relaxed line-clamp-3">
                      {blog.content
                        ? blog.content
                          .replace(/&lt;/gi, '<')
                          .replace(/&gt;/gi, '>')
                          .replace(/&nbsp;/gi, ' ')
                          .replace(/<[^>]+>/g, ' ')
                          .trim()
                        : ''}
                    </div>
                    <Link href={`/insights/${blog.slug || blog._id}?from=home`} aria-label={`Read more about ${blog.title}`} className="text-[12px] font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1 hover:text-[#0d9488] transition-colors mt-auto">
                      READ MORE<span className="sr-only"> about {blog.title}</span> <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center ml-1"><ChevronRight className="w-3 h-3" /></div>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-1 md:col-span-3 flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="w-16 h-16 mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H14" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-serif text-gray-900 mb-2">No Insights Available</h3>
                <p className="text-gray-500 text-sm">Check back later for new updates and insights.</p>
              </div>
            )}

          </div>

          <div className="flex justify-center">
            <Link
              href="/insights"
              className="inline-flex items-center justify-center px-8 py-3 rounded-full border border-[#0f766e] text-[#0f766e] font-bold text-[14px] hover:bg-[#0f766e] hover:text-white transition-colors"
            >
              See More Insights
            </Link>
          </div>

        </div>
      </section>

    </div>
  );
}
